/**
 * Quick Link "scope" cookie.
 *
 * When a customer lands on /shop/[token] for a *services*-type link we
 * stamp this cookie so every subsequent page (accessories, /book, /gift-cards,
 * etc.) knows it should render the scoped Header — the customer can't
 * tab their way into /wigs / /builder via the global nav.
 *
 * The cookie is NOT a security boundary; a determined customer can still
 * type any URL. This is purely a "don't tempt the customer with the rest
 * of the catalog" UX gate. We therefore skip HMAC signing — the worst a
 * forged cookie can do is hide *more* nav links from the attacker.
 */

export const QL_SCOPE_COOKIE = "ql_scope";

export interface QuickLinkScope {
  token: string;
  linkType: "products" | "services";
  features: string[];
  /** Original /shop/[token] URL — used by "Back to your link" pill. */
  source: string;
  /** Unix ms when the scope expires (matches link expiry or +24h). */
  expiresAt: number;
}

const FEATURES_WHITELIST = new Set(["accessories", "book", "gift_cards", "service_pickup"]);

function isStringArray(v: unknown): v is string[] {
  return Array.isArray(v) && v.every((x) => typeof x === "string");
}

export function encodeScope(scope: QuickLinkScope): string {
  return encodeURIComponent(JSON.stringify(scope));
}

export function decodeScope(raw: string | undefined | null): QuickLinkScope | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(decodeURIComponent(raw)) as Partial<QuickLinkScope>;
    if (
      typeof parsed.token !== "string" ||
      (parsed.linkType !== "products" && parsed.linkType !== "services") ||
      !isStringArray(parsed.features) ||
      typeof parsed.source !== "string" ||
      typeof parsed.expiresAt !== "number"
    ) {
      return null;
    }
    if (Date.now() > parsed.expiresAt) return null;
    // Strip any unknown features — defensive against an old cookie
    // surviving a feature rename.
    const features = parsed.features.filter((f) => FEATURES_WHITELIST.has(f));
    return {
      token: parsed.token,
      linkType: parsed.linkType,
      features,
      source: parsed.source,
      expiresAt: parsed.expiresAt,
    };
  } catch {
    return null;
  }
}

/**
 * Read the cookie on the client. SSR returns null (cookie isn't readable
 * during prerender) — components must hydrate, so this is fine.
 */
export function readClientScope(): QuickLinkScope | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(new RegExp(`(?:^|; )${QL_SCOPE_COOKIE}=([^;]*)`));
  return decodeScope(match?.[1]);
}

// Path → scope-feature mapping. Mirrors SCOPE_FEATURE_HREFS in
// Header.tsx — kept here so quick-link-scope.ts is the single source
// of truth for "is this path inside a scoped flow?".
const FEATURE_PATH_PREFIXES: Record<string, string> = {
  book: "/book",
  accessories: "/accessories",
  service_pickup: "/account/service-pickup",
  gift_cards: "/gift-cards",
};

/**
 * Returns the scope ONLY if the current path actually belongs inside
 * the scoped flow — any /shop/[token] page, the original source URL,
 * or one of the feature paths the link grants access to.
 *
 * Without this gate, a customer who visited a /shop/{token} link and
 * then navigated back to / would still see the stripped-down "Private
 * link · Curated for you" footer + scoped header on the public site,
 * because the cookie persists across navigation. Bug 2026-04-29.
 */
export function getActiveScope(
  scope: QuickLinkScope | null,
  pathname: string | null | undefined
): QuickLinkScope | null {
  if (!scope || !pathname) return null;
  // Strip /[locale] prefix so /es/book and /book both match.
  const path = pathname.replace(/^\/[a-z]{2}(?=\/|$)/, "") || "/";
  if (path.startsWith("/shop/")) return scope;
  if (scope.source && path === scope.source.replace(/^\/[a-z]{2}(?=\/|$)/, "")) {
    return scope;
  }
  for (const feature of scope.features) {
    const prefix = FEATURE_PATH_PREFIXES[feature];
    if (prefix && (path === prefix || path.startsWith(prefix + "/"))) {
      return scope;
    }
  }
  return null;
}

/** Drop the cookie client-side. Caller is responsible for navigation. */
export function clearClientScope(): void {
  if (typeof document === "undefined") return;
  document.cookie = `${QL_SCOPE_COOKIE}=; Path=/; Max-Age=0; SameSite=Lax`;
}

/**
 * Build the Set-Cookie header value. Caller appends to a NextResponse.
 * Not httpOnly — Header reads it client-side. SameSite=Lax so it survives
 * normal in-app navigation but doesn't ride along on cross-site requests.
 */
export function buildScopeCookie(scope: QuickLinkScope): string {
  const maxAgeSec = Math.max(60, Math.floor((scope.expiresAt - Date.now()) / 1000));
  const value = encodeScope(scope);
  const secure = process.env.NODE_ENV === "production" ? "; Secure" : "";
  return `${QL_SCOPE_COOKIE}=${value}; Path=/; Max-Age=${maxAgeSec}; SameSite=Lax${secure}`;
}

/** Build a Set-Cookie header that immediately clears the cookie. */
export function buildClearScopeCookie(): string {
  const secure = process.env.NODE_ENV === "production" ? "; Secure" : "";
  return `${QL_SCOPE_COOKIE}=; Path=/; Max-Age=0; SameSite=Lax${secure}`;
}
